import { createPortal } from 'react-dom'
import { X, CheckCircle2, AlertCircle, AlertTriangle, Info } from 'lucide-react'
import { useToastStore, type ToastVariant } from '@/store/toast'
import { cn } from '@/utils/cn'

const ICONS: Record<ToastVariant, React.ElementType> = {
  success: CheckCircle2,
  error:   AlertCircle,
  warning: AlertTriangle,
  info:    Info,
}

const STYLES: Record<ToastVariant, string> = {
  success: 'border-success/30 bg-surface text-success',
  error:   'border-danger/30 bg-surface text-danger',
  warning: 'border-warning/30 bg-surface text-warning',
  info:    'border-info/30 bg-surface text-info',
}

export function Toaster() {
  const toasts = useToastStore((s) => s.toasts)
  const dismiss = useToastStore((s) => s.dismiss)

  if (toasts.length === 0) return null

  return createPortal(
    <div
      aria-live="polite"
      className="fixed bottom-4 end-4 z-50 flex flex-col gap-2"
    >
      {toasts.map((t) => {
        const Icon = ICONS[t.variant]
        return (
          <div
            key={t.id}
            role="alert"
            className={cn(
              'flex w-80 items-start gap-3 rounded border px-4 py-3 shadow-md',
              'animate-in slide-in-from-bottom-4 fade-in duration-200',
              STYLES[t.variant]
            )}
          >
            <Icon className="mt-0.5 h-4 w-4 shrink-0" />
            <p className="flex-1 text-sm text-muted-fg">{t.message}</p>
            <button
              onClick={() => dismiss(t.id)}
              className="shrink-0 text-muted hover:text-muted-fg"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )
      })}
    </div>,
    document.body
  )
}
